import React, {useLayoutEffect,useState} from 'react';
import {createPortal} from 'react-dom';

/** A display-only suffix outside contenteditable: it cannot enter stored text,
 * selection offsets, clipboard text or the screenplay's measured geometry. */
export function ScriptCompletionGhost({anchor,text,suffix,composing,readOnly}: {
    anchor:React.RefObject<HTMLDivElement|null>;text:string;suffix?:string;
    composing:React.RefObject<boolean>;readOnly:boolean;
}) {
    const [position,setPosition]=useState<React.CSSProperties|null>(null);
    useLayoutEffect(()=>{
        const editor=anchor.current;
        if(!editor||!suffix||readOnly){setPosition(null);return;}
        const update=()=>{
            const selection=window.getSelection();
            if(composing.current||document.activeElement!==editor||editor.textContent!==text
                ||!selection?.rangeCount||!selection.isCollapsed){setPosition(null);return;}
            const caret=selection.getRangeAt(0);
            if(!editor.contains(caret.startContainer)){setPosition(null);return;}
            const prefix=caret.cloneRange();prefix.selectNodeContents(editor);prefix.setEnd(caret.startContainer,caret.startOffset);
            if(prefix.toString().length!==text.length){setPosition(null);return;}
            const rect=caret.getBoundingClientRect(),bounds=editor.getBoundingClientRect();
            const width=Math.min(bounds.right,window.innerWidth)-rect.right;
            if(!rect.height||width<1||rect.top<0||rect.bottom>window.innerHeight){setPosition(null);return;}
            const style=getComputedStyle(caret.startContainer.parentElement??editor);
            setPosition({position:'fixed',left:rect.right,top:rect.top,width,height:rect.height,
                font:style.font,lineHeight:style.lineHeight,color:'#929292',whiteSpace:'pre',
                overflow:'hidden',pointerEvents:'none',userSelect:'none',zIndex:35});
        };
        const hide=()=>setPosition(null);
        const frame=requestAnimationFrame(update);
        document.addEventListener('selectionchange',update);
        document.addEventListener('focusin',update);
        editor.addEventListener('compositionstart',hide);
        window.addEventListener('scroll',update,true);window.addEventListener('resize',update);
        const observer=new ResizeObserver(update);observer.observe(editor);
        return ()=>{
            cancelAnimationFrame(frame);observer.disconnect();
            document.removeEventListener('selectionchange',update);document.removeEventListener('focusin',update);
            editor.removeEventListener('compositionstart',hide);
            window.removeEventListener('scroll',update,true);window.removeEventListener('resize',update);
        };
    },[anchor,text,suffix,composing,readOnly]);
    return position&&suffix&&!readOnly?createPortal(
        <span aria-hidden="true" data-script-completion-ghost style={position}>{suffix}</span>,document.body):null;
}
